import apiClient from './api';
import { bufferListeningTime, getAndClearBuffer } from './listeningTimeBuffer';

export interface LanguageListeningTime {
  language: string;
  totalSeconds: number;
  monthlySeconds: number;
}

export interface ListeningTimeStats {
  languages: LanguageListeningTime[];
}

export interface TrackListeningTimePayload {
  seconds: number;
  language: string;
  timezone?: string;
  media_type?: string;
}

export interface DailyListeningTimeData {
  date: string;
  language: string;
  totalSeconds: number;
}

export interface ListeningTimeReportParams {
  startDate: string;
  endDate: string;
}

export const listeningTimeService = {
  // Track listening time — buffers to localStorage on failure so reconnect can drain it
  async track(payload: TrackListeningTimePayload): Promise<{ status: string; message: string }> {
    // Get user's timezone if not provided
    const timezone = payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    try {
      const response = await apiClient.post<{ status: string; message: string }>(
        '/track/listening-time',
        { ...payload, timezone },
      );
      return response.data;
    } catch (err) {
      // Network failure or degraded connection — persist locally for reconnect drain
      bufferListeningTime({ seconds: payload.seconds, language: payload.language, media_type: payload.media_type });
      throw err;
    }
  },

  /**
   * Drain any buffered (offline) entries by summing them per language+media_type
   * and sending a single consolidated request each. Called on window 'online' event.
   * Failures are silently ignored — entries remain cleared to avoid double-counting.
   */
  async drainBuffer(): Promise<void> {
    const entries = getAndClearBuffer();
    if (entries.length === 0) return;

    // Consolidate: sum seconds per { language, media_type } bucket
    const buckets = new Map<string, { seconds: number; language: string; media_type?: string }>();
    for (const entry of entries) {
      const key = `${entry.language}|${entry.media_type ?? ''}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.seconds += entry.seconds;
      } else {
        buckets.set(key, { seconds: entry.seconds, language: entry.language, media_type: entry.media_type });
      }
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Fire-and-forget each bucket — individual failures are silent (no re-buffer on drain)
    await Promise.allSettled(
      Array.from(buckets.values()).map((b) =>
        apiClient.post('/track/listening-time', { ...b, timezone }),
      ),
    );
  },

  // Get listening time statistics
  async getStats(): Promise<ListeningTimeStats> {
    const response = await apiClient.get<ListeningTimeStats>('/stats/listening-time');
    return response.data;
  },

  // Get listening time report for date range
  async getReport(params: ListeningTimeReportParams): Promise<DailyListeningTimeData[]> {
    const response = await apiClient.get<DailyListeningTimeData[]>('/reports/listening-time', {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
      },
    });
    return response.data;
  },
};
